import { C } from "@bd/content";
import { artRng, Bitmap, hex, mix, OUTLINE, shade, type RGB } from "./bitmap";
import { drawSmallText, drawText, FONT_H, SMALL_H, smallTextWidth, textWidth } from "./pixelfont";

/**
 * Box covers, 96×72. Layers, back to front: a sky in the theme's colours, a scene (hills, sea, a city, a stage...),
 * the theme's hero motif standing in it, the full title (wrapped onto two lines), a subtitle ribbon for follow-ups,
 * the publisher's mark, an award seal, and a frame that says who the game is for. Same options, same cover.
 */
export const BOX_W = 96;
export const BOX_H = 72;
/** frame band width; the art lives inside it */
const F = 4;
/** widest a title line may be */
export const TITLE_W = BOX_W - 2 * F - 4;

const WHITE: RGB = [255, 255, 255];
const CREAM: RGB = [250, 244, 230];
const lum = (c: RGB) => 0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2];
const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

// ------------------------------------------------------------------ motifs: small drawings on a 32×32 canvas

/**
 * Inks a motif draws with. Theme inks: m main, s main in shadow, a accent, d dark detail, h highlight.
 * Natural inks for things that have a colour of their own: k ink-black, w white, y gold, g leaf green,
 * b wood brown, r red, q grey. x cuts a hole.
 */
type Ink = "m" | "s" | "a" | "d" | "h" | "k" | "w" | "y" | "g" | "b" | "r" | "q" | "x";
type Pt = readonly [number, number];
type Inks = Record<Exclude<Ink, "x">, RGB>;
const MS = 32;

class Pen {
  readonly b = new Bitmap(MS, MS);
  private mask = new Bitmap(MS, MS);
  constructor(private inks: Inks) {}

  private put(x: number, y: number, k: Ink, a = 255): void {
    if (k === "x") this.b.clear(x, y);
    else this.b.set(x, y, this.inks[k], a);
  }

  private stamp(draw: (m: Bitmap) => void, k: Ink, a = 255): void {
    this.mask.data.fill(0);
    draw(this.mask);
    for (let y = 0; y < MS; y++) for (let x = 0; x < MS; x++) if (this.mask.data[(y * MS + x) * 4 + 3]) this.put(x, y, k, a);
  }

  /** ellipse in the inclusive box */
  e(x0: number, y0: number, x1: number, y1: number, k: Ink, a = 255): void {
    this.stamp((m) => m.ellipse(x0, y0, x1, y1, WHITE), k, a);
  }

  /** circle */
  o(cx: number, cy: number, r: number, k: Ink, a = 255): void {
    this.e(cx - r, cy - r, cx + r, cy + r, k, a);
  }

  p(pts: readonly Pt[], k: Ink, a = 255): void {
    this.stamp((m) => m.poly(pts, WHITE), k, a);
  }

  r(x: number, y: number, w: number, h: number, k: Ink, a = 255): void {
    this.stamp((m) => m.rect(x, y, w, h, WHITE), k, a);
  }

  /** a line w pixels thick */
  l(x0: number, y0: number, x1: number, y1: number, k: Ink, w = 1): void {
    this.stamp((m) => {
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
      for (let i = 0; i <= n; i++) m.rect(Math.round(x0 + ((x1 - x0) * i) / n - (w - 1) / 2), Math.round(y0 + ((y1 - y0) * i) / n - (w - 1) / 2), w, w, WHITE);
    }, k);
  }

  /** a straight bar w wide from one point to another, flat ends */
  bar(x0: number, y0: number, x1: number, y1: number, w: number, k: Ink): void {
    const len = Math.hypot(x1 - x0, y1 - y0) || 1;
    const nx = (-(y1 - y0) / len) * (w / 2);
    const ny = ((x1 - x0) / len) * (w / 2);
    this.p([[x0 + nx, y0 + ny], [x1 + nx, y1 + ny], [x1 - nx, y1 - ny], [x0 - nx, y0 - ny]], k);
  }

  px(x: number, y: number, k: Ink): void {
    this.put(x, y, k);
  }

  /** paint every pixel f says to */
  fn(f: (x: number, y: number) => Ink | null): void {
    for (let y = 0; y < MS; y++) for (let x = 0; x < MS; x++) {
      const k = f(x, y);
      if (k) this.put(x, y, k);
    }
  }

  /** recolour the pixels already drawn, where f says to (stripes, patterns) */
  over(f: (x: number, y: number) => Ink | null): void {
    for (let y = 0; y < MS; y++) for (let x = 0; x < MS; x++) {
      if (!this.b.alpha(x, y)) continue;
      const k = f(x, y);
      if (k) this.put(x, y, k);
    }
  }

  text(x: number, y: number, s: string, k: Ink): void {
    this.stamp((m) => drawText(m, x, y, s, WHITE), k);
  }

  star(cx: number, cy: number, r: number, k: Ink): void {
    this.p(starPts(cx, cy, r, r * 0.45, 5), k);
  }

  hexagon(cx: number, cy: number, r: number, k: Ink): void {
    const pts: Pt[] = [];
    for (let i = 0; i < 6; i++) pts.push([cx + r * Math.cos((Math.PI / 3) * i), cy + r * Math.sin((Math.PI / 3) * i)]);
    this.p(pts, k);
  }
}

function starPts(cx: number, cy: number, ro: number, ri: number, n: number): Pt[] {
  const pts: Pt[] = [];
  for (let i = 0; i < n * 2; i++) {
    const r = i % 2 ? ri : ro;
    const a = -Math.PI / 2 + (Math.PI / n) * i;
    pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  return pts;
}

interface Motif {
  draw: (p: Pen) => void;
  /** floats in the sky rather than standing on the ground */
  fly?: boolean;
}

/** Every theme's "motif" field names one of these. Each is its own drawing: no stand-ins. */
export const MOTIFS: Record<string, Motif> = {
  paw: { draw: (p) => {
    p.e(7, 15, 24, 30, "m");
    p.e(1, 8, 8, 18, "m");
    p.e(8, 1, 15, 12, "m");
    p.e(16, 1, 23, 12, "m");
    p.e(23, 8, 30, 18, "m");
    p.e(11, 19, 16, 22, "h");
  } },
  cat: { draw: (p) => {
    p.l(24, 29, 29, 23, "s", 3);
    p.l(29, 23, 28, 15, "s", 3);
    p.e(6, 14, 25, 31, "m");
    p.e(11, 20, 20, 31, "h");
    p.e(6, 3, 25, 19, "m");
    p.p([[6, 10], [7, 0], [15, 5]], "m");
    p.p([[25, 10], [24, 0], [16, 5]], "m");
    p.p([[9, 7], [9, 3], [13, 5]], "a");
    p.p([[22, 7], [22, 3], [18, 5]], "a");
    p.r(10, 9, 3, 4, "k");
    p.r(19, 9, 3, 4, "k");
    p.px(11, 10, "w");
    p.px(20, 10, "w");
    p.r(15, 13, 2, 2, "a");
    p.px(14, 16, "d");
    p.px(17, 16, "d");
    p.r(15, 15, 2, 1, "d");
  } },
  flower: { draw: (p) => {
    p.l(16, 18, 16, 31, "g", 2);
    p.e(5, 22, 15, 26, "g");
    p.e(17, 25, 26, 29, "g");
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i - Math.PI / 2;
      const x = 16 + 8 * Math.cos(a);
      const y = 11 + 8 * Math.sin(a);
      p.e(x - 5, y - 5, x + 5, y + 5, "m");
    }
    p.o(16, 11, 4, "a");
    p.px(15, 10, "h");
  } },
  barn: { draw: (p) => {
    p.r(4, 14, 24, 18, "a");
    p.p([[1, 15], [6, 6], [16, 1], [26, 6], [31, 15]], "m");
    p.l(1, 15, 6, 6, "s");
    p.l(26, 6, 31, 15, "s");
    p.r(14, 7, 4, 4, "k");
    p.r(11, 20, 10, 12, "w");
    p.l(11, 20, 20, 31, "a");
    p.l(20, 20, 11, 31, "a");
    p.b.strokeBox(11, 20, 20, 31, [250, 244, 230]);
    p.r(6, 18, 3, 3, "w");
    p.r(23, 18, 3, 3, "w");
  } },
  pot: { draw: (p) => {
    p.l(9, 0, 8, 2, "w");
    p.l(8, 2, 9, 4, "w");
    p.l(22, 0, 21, 2, "w");
    p.l(21, 2, 22, 4, "w");
    p.r(0, 17, 4, 3, "d");
    p.r(28, 17, 4, 3, "d");
    p.r(4, 14, 24, 10, "m");
    p.e(4, 16, 27, 31, "m");
    p.r(2, 13, 28, 3, "s");
    p.e(6, 7, 25, 14, "a");
    p.e(13, 4, 18, 8, "a");
    p.r(8, 22, 16, 2, "a");
  } },
  train: { draw: (p) => {
    p.e(0, 0, 9, 3, "w", 220);
    p.r(3, 11, 17, 11, "m");
    p.e(0, 11, 6, 21, "m");
    p.r(9, 11, 2, 11, "s");
    p.r(5, 4, 5, 8, "d");
    p.r(4, 3, 7, 2, "d");
    p.e(12, 7, 16, 12, "a");
    p.r(19, 6, 12, 16, "a");
    p.r(22, 9, 6, 6, "w");
    p.r(18, 4, 14, 3, "d");
    p.r(1, 21, 30, 3, "d");
    p.p([[0, 29], [4, 22], [4, 29]], "d");
    for (const x of [8, 16, 25]) {
      p.o(x, 26, 4, "k");
      p.o(x, 26, 1, "q");
    }
  } },
  ship: { draw: (p) => {
    p.r(10, 1, 2, 21, "b");
    p.r(21, 4, 2, 18, "b");
    p.p([[11, 0], [17, 1], [11, 3]], "a");
    p.p([[3, 4], [18, 4], [17, 11], [4, 11]], "w");
    p.p([[2, 12], [19, 12], [18, 19], [3, 19]], "w");
    p.p([[22, 7], [30, 7], [29, 17], [22, 17]], "w");
    p.p([[0, 20], [31, 20], [27, 30], [5, 30]], "a");
    p.r(0, 19, 32, 2, "b");
    p.r(25, 15, 7, 5, "a");
    p.r(5, 24, 23, 1, "d");
    for (const x of [8, 14, 20]) p.o(x, 27, 1, "k");
  } },
  rocket: { fly: true, draw: (p) => {
    p.p([[12, 27], [20, 27], [16, 32]], "y");
    p.p([[14, 27], [18, 27], [16, 30]], "w");
    p.p([[10, 16], [3, 27], [10, 25]], "a");
    p.p([[21, 16], [28, 27], [21, 25]], "a");
    p.e(9, 0, 22, 28, "m");
    p.p([[11, 4], [16, 0], [20, 4]], "a");
    p.e(10, 0, 21, 8, "a");
    p.r(12, 25, 8, 3, "d");
    p.o(15.5, 13, 4, "d");
    p.o(15.5, 13, 2.5, "h");
    p.px(14, 12, "w");
    p.r(15, 17, 2, 8, "s");
  } },
  magnifier: { draw: (p) => {
    p.bar(18, 18, 29, 29, 5, "b");
    p.o(29, 29, 2.5, "b");
    p.o(12, 12, 11, "m");
    p.o(12, 12, 8, "h", 255);
    p.e(6, 6, 10, 9, "w");
    p.px(15, 17, "w");
  } },
  ghost: { fly: true, draw: (p) => {
    p.e(5, 1, 26, 22, "m");
    p.r(5, 11, 22, 15, "m");
    for (const x of [7, 13, 19, 25]) p.o(x, 26, 3, "m");
    p.o(10, 29, 2, "x");
    p.o(16, 29, 2, "x");
    p.o(22, 29, 2, "x");
    p.e(1, 12, 7, 16, "m");
    p.e(24, 12, 30, 16, "m");
    p.e(9, 7, 13, 13, "k");
    p.e(18, 7, 22, 13, "k");
    p.e(13, 15, 18, 20, "k");
    p.px(10, 8, "w");
    p.px(19, 8, "w");
    p.e(6, 4, 12, 6, "h");
  } },
  ball: { draw: (p) => {
    p.o(16, 16, 14, "m");
    p.over((x, y) => {
      const cx = x + 0.5;
      const cy = y + 0.5;
      const a = Math.hypot(cx - -4, cy - 16);
      const b = Math.hypot(cx - 36, cy - 16);
      if (Math.abs(a - 16) < 0.9 || Math.abs(b - 16) < 0.9) return "a";
      if (Math.abs(cx - 16) < 0.9 || Math.abs(cy - 16) < 0.9) return "a";
      return null;
    });
    p.e(7, 6, 12, 9, "w");
  } },
  castle: { draw: (p) => {
    p.r(8, 15, 16, 17, "s");
    for (const x of [8, 12, 16, 20]) p.r(x, 13, 2, 2, "s");
    p.r(1, 11, 8, 21, "m");
    p.r(23, 11, 8, 21, "m");
    p.r(11, 7, 10, 25, "m");
    p.p([[0, 12], [4.5, 1], [9, 12]], "a");
    p.p([[22, 12], [26.5, 1], [31, 12]], "a");
    p.p([[10, 8], [15.5, -2], [21, 8]], "a");
    p.r(15, 0, 1, 3, "d");
    p.r(4, 15, 2, 4, "k");
    p.r(26, 15, 2, 4, "k");
    p.r(15, 11, 2, 4, "k");
    p.e(12, 22, 19, 29, "k");
    p.r(12, 26, 8, 6, "k");
  } },
  sword: { draw: (p) => {
    p.bar(10, 21, 26, 5, 5, "m");
    p.p([[24, 3], [31, 0], [28, 7]], "m");
    p.l(12, 19, 26, 5, "h");
    p.bar(5, 15, 16, 26, 3, "a");
    p.bar(10, 21, 4, 27, 3, "b");
    p.o(3, 28, 2.5, "a");
  } },
  longship: { draw: (p) => {
    p.r(15, 0, 2, 19, "b");
    p.r(7, 2, 18, 13, "w");
    p.over((x, y) => (y >= 2 && y < 15 && x >= 7 && x < 25 && Math.floor((x - 7) / 3) % 2 === 1 ? "a" : null));
    p.r(15, 0, 2, 2, "b");
    p.p([[2, 17], [30, 17], [27, 26], [5, 26]], "b");
    p.l(4, 21, 1, 11, "b", 3);
    p.e(0, 7, 6, 12, "b");
    p.px(2, 9, "y");
    p.l(28, 21, 30, 12, "b", 3);
    p.e(28, 9, 31, 13, "b");
    for (let i = 0; i < 6; i++) {
      p.o(6 + i * 4, 19, 2, i % 2 ? "a" : "m");
      p.px(6 + i * 4, 19, "y");
    }
    p.r(7, 23, 19, 1, "k");
  } },
  chip: { draw: (p) => {
    for (const k of [7, 11, 15, 19, 23]) {
      p.r(k, 1, 2, 4, "q");
      p.r(k, 27, 2, 4, "q");
      p.r(1, k, 4, 2, "q");
      p.r(27, k, 4, 2, "q");
    }
    p.r(5, 5, 22, 22, "m");
    p.r(10, 10, 12, 12, "a");
    p.r(12, 12, 8, 8, "h");
    p.o(8, 8, 1, "d");
    p.l(12, 16, 7, 16, "s");
    p.l(16, 22, 16, 25, "s");
    p.l(22, 13, 25, 13, "s");
  } },
  dino: { draw: (p) => {
    p.p([[0, 25], [9, 16], [11, 25]], "m");
    p.r(8, 23, 4, 9, "s");
    p.r(18, 23, 4, 9, "s");
    p.e(5, 12, 25, 27, "m");
    p.bar(20, 18, 25, 5, 5, "m");
    p.e(21, 1, 31, 8, "m");
    p.r(10, 24, 4, 8, "m");
    p.r(20, 24, 4, 8, "m");
    p.px(27, 3, "k");
    p.px(30, 5, "d");
    for (const [x, y] of [[10, 15], [14, 14], [18, 15], [12, 19], [17, 19]] as const) p.o(x, y, 1, "a");
    p.e(9, 21, 21, 26, "h");
  } },
  fish: { draw: (p) => {
    p.p([[21, 16], [31, 6], [28, 16], [31, 26]], "a");
    p.p([[9, 9], [15, 2], [20, 10]], "a");
    p.p([[11, 22], [14, 29], [18, 22]], "a");
    p.e(1, 7, 25, 25, "m");
    p.e(4, 17, 22, 24, "h");
    p.o(7, 13, 2.5, "w");
    p.o(7.5, 13.5, 1, "k");
    p.l(12, 10, 13, 13, "s");
    p.l(13, 13, 12, 21, "s");
    p.r(1, 17, 3, 1, "d");
    for (const x of [16, 20]) p.l(x, 10, x, 22, "a");
  } },
  hat: { draw: (p) => {
    p.p([[5, 25], [13, 9], [17, 3], [27, 0], [21, 6], [27, 25]], "m");
    p.e(0, 21, 31, 30, "s");
    p.p([[6, 24], [14, 9], [16, 6]], "h");
    p.r(7, 19, 19, 4, "a");
    p.r(14, 19, 4, 4, "y");
    p.star(12, 13, 3, "y");
    p.star(20, 9, 2, "y");
    p.px(19, 15, "w");
  } },
  monster: { draw: (p) => {
    p.p([[6, 10], [2, 0], [11, 6]], "a");
    p.p([[25, 10], [29, 0], [20, 6]], "a");
    p.e(6, 26, 13, 31, "s");
    p.e(18, 26, 25, 31, "s");
    p.e(3, 4, 28, 29, "m");
    p.e(1, 14, 6, 20, "m");
    p.e(25, 14, 30, 20, "m");
    p.o(16, 13, 6, "w");
    p.o(17, 14, 3, "k");
    p.px(16, 12, "w");
    p.e(8, 20, 23, 26, "k");
    for (const x of [10, 14, 18]) p.p([[x, 20], [x + 3, 20], [x + 1.5, 23]], "w");
    p.e(9, 6, 13, 8, "h");
  } },
  star: { draw: (p) => {
    p.p(starPts(16, 17, 15, 7, 5), "m");
    for (let i = 0; i < 5; i++) {
      const a = -Math.PI / 2 + ((Math.PI * 2) / 5) * i;
      p.o(16 + 14 * Math.cos(a), 17 + 14 * Math.sin(a), 2, "m");
    }
    p.o(16, 17, 5, "a");
    p.o(16, 17, 3, "h");
    p.px(15, 16, "w");
  } },
  pyramid: { draw: (p) => {
    p.p([[5, 13], [13, 27], [-3, 27]], "s");
    p.p([[16, 2], [32, 30], [0, 30]], "m");
    p.p([[16, 2], [32, 30], [16, 30]], "s");
    for (const y of [10, 16, 22]) p.l(16 - (y - 2) * 0.55, y, 15, y, "h");
    for (const y of [10, 16, 22]) p.l(17, y, 16 + (y - 2) * 0.55, y, "m");
    p.r(14, 25, 4, 5, "k");
    p.p([[14, 25], [16, 23], [18, 25]], "k");
  } },
  column: { draw: (p) => {
    p.r(2, 2, 28, 3, "m");
    p.r(5, 5, 22, 3, "s");
    p.o(5, 8, 3, "m");
    p.o(26, 8, 3, "m");
    p.px(5, 8, "d");
    p.px(26, 8, "d");
    p.r(8, 8, 16, 19, "m");
    for (const x of [10, 13, 16, 19, 22]) p.r(x, 9, 1, 17, "s");
    p.r(6, 27, 20, 2, "m");
    p.r(3, 29, 26, 3, "s");
    p.l(21, 10, 23, 14, "d");
    p.l(23, 14, 22, 17, "d");
  } },
  leaf: { draw: (p) => {
    const S: Pt = [3, 29];
    const T: Pt = [30, 2];
    const up: Pt[] = [];
    const dn: Pt[] = [];
    const len = Math.hypot(T[0] - S[0], T[1] - S[1]);
    const nx = -(T[1] - S[1]) / len;
    const ny = (T[0] - S[0]) / len;
    for (let i = 0; i <= 16; i++) {
      const t = i / 16;
      const w = Math.sin(Math.PI * Math.pow(t, 0.8)) * 10;
      const cx = S[0] + (T[0] - S[0]) * t;
      const cy = S[1] + (T[1] - S[1]) * t;
      up.push([cx + nx * w, cy + ny * w]);
      dn.unshift([cx - nx * w, cy - ny * w]);
    }
    p.p([...up, ...dn], "m");
    p.l(0, 31, 4, 28, "b", 2);
    p.l(4, 28, 29, 3, "s");
    for (let i = 1; i < 5; i++) {
      const t = i / 5;
      const cx = S[0] + (T[0] - S[0]) * t;
      const cy = S[1] + (T[1] - S[1]) * t;
      p.l(cx, cy, cx + nx * 6 + 3, cy + ny * 6 - 3, "s");
      p.l(cx, cy, cx - nx * 6 + 3, cy - ny * 6 - 3, "s");
    }
  } },
  mountain: { draw: (p) => {
    p.p([[-2, 31], [9, 11], [20, 31]], "s");
    p.p([[6, 15], [9, 11], [12, 15], [10, 14], [9, 16], [8, 14]], "w");
    p.p([[5, 31], [19, 3], [33, 31]], "m");
    p.p([[19, 3], [33, 31], [23, 31]], "s");
    p.p([[14, 13], [19, 3], [24, 13], [22, 11], [20, 14], [18, 11], [16, 14]], "w");
  } },
  tent: { draw: (p) => {
    p.r(16, 0, 1, 4, "d");
    p.p([[17, 0], [22, 1], [17, 3]], "a");
    p.p([[16, 3], [31, 15], [1, 15]], "m");
    p.r(3, 15, 26, 17, "m");
    p.over((x, y) => {
      if (y < 15) return Math.floor(Math.atan2(x + 0.5 - 16, y + 0.5 - 3) / 0.32) % 2 === 0 ? "a" : null;
      return Math.floor(Math.abs(x + 0.5 - 16) / 4) % 2 === 0 ? "a" : null;
    });
    for (let x = 2; x < 31; x += 4) p.o(x + 1, 15, 2, "y");
    p.p([[12, 32], [16, 20], [20, 32]], "k");
  } },
  note: { draw: (p) => {
    p.r(9, 5, 3, 22, "m");
    p.r(25, 1, 3, 22, "m");
    p.p([[9, 5], [28, 1], [28, 6], [9, 10]], "m");
    p.e(1, 22, 12, 30, "m");
    p.e(17, 18, 28, 26, "m");
    p.e(3, 23, 7, 25, "h");
    p.e(19, 19, 23, 21, "h");
    p.l(10, 6, 27, 2, "h");
  } },
  film: { draw: (p) => {
    p.r(2, 13, 28, 18, "m");
    p.r(4, 18, 24, 1, "s");
    p.r(5, 21, 10, 2, "d");
    p.r(18, 21, 8, 2, "d");
    p.r(5, 25, 14, 2, "d");
    p.r(22, 25, 4, 2, "a");
    p.r(2, 8, 28, 5, "k");
    p.p([[2, 7], [28, -1], [30, 4], [3, 12]], "k");
    p.over((x, y) => (y < 13 && Math.floor((x + y * (y < 8 || x > 22 ? 0.9 : 0.6)) / 4) % 2 === 0 ? "w" : null));
    p.o(3, 10, 1.5, "q");
  } },
  flag: { draw: (p) => {
    p.r(2, 2, 2, 30, "b");
    p.o(3, 1, 2, "y");
    p.fn((x, y) => {
      if (x < 4 || x > 30) return null;
      const dy = Math.round(2 * Math.sin((x - 4) / 4.5));
      const yy = y - 3 - dy;
      if (yy < 0 || yy > 17) return null;
      return (Math.floor((x - 4) / 4) + Math.floor(yy / 4.5)) % 2 ? "k" : "w";
    });
  } },
  plane: { fly: true, draw: (p) => {
    p.p([[24, 13], [28, 3], [31, 3], [31, 15]], "a");
    p.e(2, 11, 28, 21, "m");
    p.p([[20, 12], [31, 13], [31, 17], [20, 20]], "m");
    p.r(7, 6, 15, 3, "a");
    p.r(5, 17, 18, 3, "a");
    p.r(10, 9, 1, 8, "d");
    p.r(18, 9, 1, 8, "d");
    p.e(13, 9, 18, 13, "h");
    p.r(0, 9, 2, 15, "d");
    p.o(2, 16, 2, "y");
    p.l(9, 20, 8, 25, "d");
    p.l(18, 20, 19, 25, "d");
    p.o(8, 26, 2.5, "k");
    p.o(19, 26, 2.5, "k");
    p.l(4, 14, 26, 14, "h");
  } },
  lighthouse: { draw: (p) => {
    p.p([[11, 4], [0, 0], [0, 9]], "y", 120);
    p.p([[21, 4], [32, 0], [32, 9]], "y", 120);
    p.e(2, 27, 29, 35, "q");
    p.p([[10, 9], [21, 9], [24, 30], [7, 30]], "w");
    p.over((x, y) => (y >= 9 && y <= 30 && (y < 14 || (y >= 18 && y < 23)) && x > 5 && x < 26 ? "a" : null));
    p.r(8, 7, 16, 2, "d");
    p.r(11, 3, 10, 4, "y");
    p.r(13, 4, 6, 2, "w");
    p.p([[10, 3], [16, -1], [22, 3]], "a");
    p.r(14, 24, 4, 6, "d");
  } },
  hex: { draw: (p) => {
    for (const [x, y] of [[7, 23], [16, 27], [25, 23], [16, 19]] as const) {
      p.hexagon(x, y, 5.5, "y");
      p.hexagon(x, y, 3.5, "a");
    }
    p.e(8, 1, 16, 9, "w", 230);
    p.e(16, 0, 25, 8, "w", 230);
    p.e(7, 6, 26, 18, "y");
    p.over((x, y) => (y >= 6 && y <= 18 && x >= 7 && x <= 26 && [12, 13, 18, 19].includes(x) ? "k" : null));
    p.o(7, 12, 4.5, "k");
    p.p([[25, 11], [30, 12], [25, 13]], "k");
    p.px(5, 11, "w");
    p.l(5, 8, 3, 4, "k");
  } },
  bird: { draw: (p) => {
    p.r(1, 28, 30, 3, "b");
    p.r(25, 26, 4, 2, "g");
    p.p([[19, 17], [31, 22], [30, 26], [17, 22]], "s");
    p.e(5, 8, 25, 25, "m");
    p.o(10, 9, 7, "m");
    p.e(4, 13, 15, 24, "a");
    p.e(12, 11, 25, 21, "s");
    p.l(15, 18, 22, 18, "d");
    p.p([[4, 7], [-1, 10], [4, 12]], "y");
    p.o(8.5, 8.5, 1.5, "k");
    p.px(8, 7, "w");
    p.l(12, 25, 11, 28, "d");
    p.l(16, 25, 17, 28, "d");
  } },
  mushroom: { draw: (p) => {
    p.e(18, 26, 30, 31, "g");
    p.p([[11, 15], [21, 15], [23, 31], [9, 31]], "w");
    p.l(19, 17, 21, 30, "q");
    p.e(1, 1, 30, 23, "r");
    p.e(3, 15, 28, 22, "q");
    p.r(1, 18, 30, 6, "x");
    p.r(9, 17, 14, 7, "w");
    p.o(8, 9, 2.5, "w");
    p.o(16, 5, 3, "w");
    p.o(23, 10, 2.5, "w");
    p.o(14, 13, 1.5, "w");
    p.e(5, 4, 9, 6, "w", 120);
  } },
  cup: { draw: (p) => {
    for (const x of [11, 17]) {
      p.l(x, 1, x - 1, 4, "w");
      p.l(x - 1, 4, x, 7, "w");
    }
    p.e(0, 25, 31, 31, "s");
    p.o(26, 18, 5, "m");
    p.o(26, 18, 2.5, "x");
    p.p([[3, 12], [27, 12], [23, 28], [7, 28]], "m");
    p.e(3, 10, 27, 15, "h");
    p.e(5, 11, 25, 14, "b");
    p.r(6, 19, 19, 2, "a");
    p.e(4, 26, 27, 29, "h");
  } },
  grape: { draw: (p) => {
    p.l(16, 0, 16, 7, "b", 2);
    p.p([[16, 4], [28, 0], [30, 6], [22, 9]], "g");
    p.l(17, 5, 27, 2, "s");
    const rows = [[7, 13, 19, 25], [10, 16, 22], [13, 19], [16]];
    rows.forEach((xs, i) => {
      for (const x of xs) {
        p.o(x, 10 + i * 6, 4, "d");
        p.o(x - 0.5, 9.5 + i * 6, 3, "a");
        p.px(x - 2, 8 + i * 6, "w");
      }
    });
  } },
  tower: { draw: (p) => {
    p.r(1, 12, 10, 20, "s");
    p.r(22, 8, 9, 24, "a");
    p.r(10, 2, 12, 30, "m");
    p.r(15, 0, 2, 3, "d");
    p.r(12, 4, 8, 1, "h");
    p.fn((x, y) => {
      const inMid = x >= 12 && x < 20 && y > 5 && y < 30 && x % 3 !== 2 && y % 3 === 0;
      const inL = x >= 3 && x < 9 && y > 14 && y < 30 && x % 3 !== 2 && y % 3 === 1;
      const inR = x >= 24 && x < 30 && y > 10 && y < 30 && x % 3 !== 2 && y % 3 === 2;
      return inMid || inL || inR ? "y" : null;
    });
    p.r(14, 27, 4, 5, "k");
  } },
  gem: { draw: (p) => {
    p.p([[8, 6], [24, 6], [31, 14], [16, 31], [1, 14]], "m");
    p.p([[11, 6], [21, 6], [24, 14], [8, 14]], "h");
    p.p([[1, 14], [8, 14], [16, 31]], "s");
    p.p([[24, 14], [31, 14], [16, 31]], "s");
    p.l(8, 14, 16, 30, "a");
    p.l(24, 14, 16, 30, "a");
    p.l(1, 14, 31, 14, "a");
    p.star(26, 4, 3, "w");
    p.px(12, 8, "w");
  } },
  ufo: { fly: true, draw: (p) => {
    p.p([[10, 22], [22, 22], [30, 32], [2, 32]], "y", 110);
    p.e(8, 2, 23, 18, "h");
    p.o(15.5, 10, 3.5, "g");
    p.px(14, 9, "k");
    p.px(17, 9, "k");
    p.e(10, 4, 13, 7, "w");
    p.e(0, 12, 31, 23, "m");
    p.e(5, 18, 26, 25, "s");
    for (const x of [5, 10, 16, 21, 26]) p.o(x, 16, 1.2, "y");
  } },
  clock: { draw: (p) => {
    p.l(8, 26, 5, 31, "d", 2);
    p.l(23, 26, 26, 31, "d", 2);
    p.o(7, 5, 4.5, "a");
    p.o(24, 5, 4.5, "a");
    p.r(15, 0, 2, 5, "d");
    p.r(12, 0, 8, 2, "d");
    p.o(15.5, 17, 12.5, "m");
    p.o(15.5, 17, 9.5, "w");
    for (const [x, y] of [[15, 8], [24, 17], [15, 26], [6, 17]] as const) p.r(x, y, 2, 1, "k");
    p.l(15.5, 17, 15.5, 10, "k", 2);
    p.l(15.5, 17, 21, 20, "k", 2);
    p.o(15.5, 17, 1, "a");
    p.e(8, 8, 12, 10, "h");
  } },
  gear: { draw: (p) => {
    for (let i = 0; i < 8; i++) {
      const a = (Math.PI / 4) * i;
      p.bar(16 + Math.cos(a) * 8, 16 + Math.sin(a) * 8, 16 + Math.cos(a) * 15.5, 16 + Math.sin(a) * 15.5, 6, "m");
    }
    p.o(16, 16, 11.5, "m");
    p.o(16, 16, 7, "s");
    p.o(16, 16, 3.5, "x");
    for (let i = 0; i < 4; i++) {
      const a = (Math.PI / 2) * i + Math.PI / 4;
      p.o(16 + Math.cos(a) * 9.5, 16 + Math.sin(a) * 9.5, 1, "a");
    }
  } },
  hand: { draw: (p) => {
    p.e(-2, 25, 33, 38, "b");
    p.r(10, 18, 12, 11, "a");
    p.p([[10, 18], [12, 16], [14, 18], [17, 15], [19, 18], [22, 16], [22, 19]], "a");
    p.r(10, 9, 13, 10, "m");
    p.r(9, 1, 3, 10, "m");
    p.r(13, 0, 3, 10, "m");
    p.r(17, 1, 3, 9, "m");
    p.r(21, 4, 3, 8, "m");
    p.p([[10, 13], [4, 8], [6, 6], [11, 10]], "m");
    for (const [x, y] of [[9, 1], [13, 0], [17, 1], [21, 4]] as const) p.r(x, y, 3, 1, "h");
    p.l(13, 12, 15, 15, "s");
    p.r(18, 13, 2, 2, "d");
    p.r(4, 27, 4, 2, "q");
    p.r(24, 28, 3, 2, "q");
  } },
  robot: { draw: (p) => {
    p.r(15, 0, 2, 5, "d");
    p.o(16, 1, 2, "a");
    p.r(1, 17, 4, 9, "s");
    p.r(27, 17, 4, 9, "s");
    p.o(3, 27, 2, "d");
    p.o(29, 27, 2, "d");
    p.r(9, 27, 5, 5, "d");
    p.r(18, 27, 5, 5, "d");
    p.r(7, 4, 18, 11, "m");
    p.r(4, 7, 3, 5, "s");
    p.r(25, 7, 3, 5, "s");
    p.r(10, 7, 4, 4, "y");
    p.r(18, 7, 4, 4, "y");
    p.r(11, 8, 2, 2, "k");
    p.r(19, 8, 2, 2, "k");
    p.r(12, 12, 8, 1, "d");
    p.r(13, 15, 6, 2, "d");
    p.r(5, 17, 22, 11, "m");
    p.r(9, 19, 14, 6, "s");
    p.px(11, 21, "r");
    p.px(14, 21, "y");
    p.px(17, 21, "g");
    p.r(11, 23, 10, 1, "h");
  } },
  bolt: { fly: true, draw: (p) => {
    p.p([[19, 0], [5, 18], [14, 18], [9, 32], [27, 11], [18, 11], [24, 0]], "y");
    p.p([[19, 1], [8, 16], [12, 16]], "w");
    p.l(21, 11, 12, 27, "a");
  } },
  fan: { draw: (p) => {
    p.fn((x, y) => {
      const dx = x + 0.5 - 16;
      const dy = y + 0.5 - 29;
      const r = Math.hypot(dx, dy);
      const a = Math.atan2(dy, dx);
      if (a > -0.5 || a < -Math.PI + 0.5 || r > 19 || r < 4) return null;
      if (r > 16) return "a";
      const seg = Math.floor((a + Math.PI) / 0.22);
      return seg % 2 ? "m" : "h";
    });
    p.fn((x, y) => {
      const dx = x + 0.5 - 16;
      const dy = y + 0.5 - 29;
      const r = Math.hypot(dx, dy);
      const a = Math.atan2(dy, dx);
      return a < -0.5 && a > -Math.PI + 0.5 && r <= 16 && r >= 4 && Math.abs(((a + Math.PI) % 0.44) - 0.22) < 0.035 ? "s" : null;
    });
    p.o(16, 18, 4, "r");
    p.l(16, 28, 16, 31, "b", 3);
    p.o(16, 29, 2, "y");
  } },
  mask: { draw: (p) => {
    p.e(2, 6, 29, 11, "k");
    p.r(8, 0, 16, 9, "k");
    p.r(8, 6, 16, 2, "a");
    p.e(1, 12, 16, 25, "m");
    p.e(15, 12, 30, 25, "m");
    p.r(12, 14, 8, 6, "m");
    p.e(5, 15, 12, 20, "x");
    p.e(19, 15, 26, 20, "x");
    p.l(1, 18, 0, 27, "a", 2);
    p.l(30, 18, 31, 27, "a", 2);
    p.l(4, 13, 12, 13, "h");
    p.l(19, 13, 27, 13, "h");
  } },
  key: { draw: (p) => {
    p.o(8, 16, 7.5, "y");
    p.o(8, 16, 3.5, "x");
    p.r(15, 14, 15, 5, "y");
    p.r(22, 19, 3, 6, "y");
    p.r(27, 19, 4, 8, "y");
    p.l(4, 11, 7, 9, "w");
    p.l(16, 15, 28, 15, "w");
  } },
  bone: { draw: (p) => {
    p.bar(7, 25, 25, 7, 7, "w");
    for (const [x, y] of [[4, 23], [9, 28], [23, 4], [28, 9]] as const) p.o(x, y, 4, "w");
    p.l(9, 21, 21, 9, "q");
    p.l(26, 4, 27, 5, "q");
  } },
  volcano: { draw: (p) => {
    p.o(15, 6, 4, "q");
    p.o(20, 3, 4, "q");
    p.o(25, 1, 3, "q");
    p.p([[1, 31], [12, 9], [20, 9], [31, 31]], "b");
    p.p([[16, 9], [20, 9], [31, 31], [20, 31]], "k", 140);
    p.e(11, 7, 21, 11, "r");
    p.l(13, 10, 9, 22, "r", 3);
    p.l(9, 22, 7, 29, "r", 2);
    p.l(18, 10, 21, 18, "r", 2);
    p.l(13, 10, 10, 20, "y");
    p.e(13, 8, 19, 9, "y");
  } },
  camel: { draw: (p) => {
    p.r(6, 21, 3, 11, "s");
    p.r(20, 21, 3, 11, "s");
    p.l(27, 15, 30, 22, "s", 2);
    p.e(5, 11, 28, 24, "m");
    p.e(8, 4, 16, 15, "m");
    p.e(16, 6, 24, 15, "m");
    p.bar(8, 17, 4, 6, 5, "m");
    p.e(0, 2, 8, 8, "m");
    p.r(0, 5, 3, 3, "m");
    p.px(4, 4, "k");
    p.r(6, 1, 2, 2, "m");
    p.r(10, 21, 3, 11, "m");
    p.r(23, 21, 3, 11, "m");
    p.p([[12, 13], [21, 13], [22, 19], [11, 19]], "a");
    for (const x of [12, 15, 18, 21]) p.px(x, 20, "y");
    p.r(12, 15, 10, 1, "y");
  } },
  cauldron: { draw: (p) => {
    p.p([[8, 30], [12, 24], [14, 30]], "y");
    p.p([[15, 31], [18, 24], [21, 31]], "r");
    p.p([[20, 30], [23, 25], [25, 30]], "y");
    p.o(10, 6, 2, "a");
    p.o(17, 2, 2.5, "a");
    p.o(22, 7, 1.5, "a");
    p.e(3, 11, 28, 29, "k");
    p.e(2, 9, 29, 15, "q");
    p.e(4, 10, 27, 14, "a");
    p.e(8, 11, 14, 13, "h");
    p.e(6, 17, 10, 24, "q", 140);
    p.l(4, 27, 2, 31, "k", 2);
    p.l(27, 27, 29, 31, "k", 2);
  } },
  bat: { fly: true, draw: (p) => {
    const wing: Pt[] = [[16, 9], [4, 2], [0, 14], [4, 12], [6, 18], [10, 14], [12, 20], [16, 15]];
    p.p(wing, "k");
    p.p(wing.map(([x, y]) => [31 - x, y] as Pt), "k");
    p.l(15, 10, 4, 3, "q");
    p.l(16, 10, 27, 3, "q");
    p.l(13, 12, 6, 17, "q");
    p.l(18, 12, 25, 17, "q");
    p.e(11, 7, 20, 22, "k");
    p.p([[11, 9], [12, 2], [15, 8]], "k");
    p.p([[20, 9], [19, 2], [16, 8]], "k");
    p.r(13, 11, 2, 2, "y");
    p.r(17, 11, 2, 2, "y");
    p.px(14, 16, "w");
    p.px(17, 16, "w");
    p.e(13, 17, 18, 21, "q");
  } },
  dragon: { draw: (p) => {
    p.bar(21, 26, 31, 19, 4, "m");
    p.p([[29, 17], [32, 15], [31, 21]], "a");
    p.p([[17, 14], [27, 0], [31, 7], [28, 9], [26, 13], [22, 15]], "a");
    p.l(18, 14, 27, 1, "d");
    p.e(8, 13, 25, 30, "m");
    p.e(9, 17, 16, 29, "h");
    p.bar(13, 18, 8, 6, 6, "m");
    p.e(1, 1, 13, 10, "m");
    p.r(0, 5, 6, 5, "m");
    p.p([[9, 3], [14, 0], [12, 5]], "y");
    p.px(7, 4, "y");
    p.px(1, 6, "k");
    p.r(11, 27, 4, 5, "s");
    p.r(19, 27, 4, 5, "s");
    for (const [x, y] of [[16, 13], [20, 15], [24, 18]] as const) p.p([[x, y], [x + 2, y - 3], [x + 3, y]], "a");
  } },
  block: { draw: (p) => {
    const cube = (x: number, y: number, face: Ink, letter: string) => {
      p.r(x, y, 14, 14, face);
      p.b.strokeBox(x + 1, y + 1, x + 12, y + 12, [255, 255, 255]);
      p.text(x + 5, y + 4, letter, "k");
    };
    cube(1, 17, "a", "A");
    cube(17, 17, "m", "B");
    cube(9, 2, "r", "C");
  } },
  cloud: { fly: true, draw: (p) => {
    for (let i = 0; i < 8; i++) {
      const a = (Math.PI / 4) * i;
      p.l(21 + Math.cos(a) * 9, 10 + Math.sin(a) * 9, 21 + Math.cos(a) * 12, 10 + Math.sin(a) * 12, "y", 2);
    }
    p.o(21, 10, 7, "y");
    p.o(9, 20, 6, "w");
    p.o(17, 16, 8, "w");
    p.o(25, 21, 5, "w");
    p.r(4, 21, 24, 7, "w");
    p.r(5, 25, 22, 3, "q");
    p.l(9, 30, 8, 31, "a");
    p.l(16, 30, 15, 31, "a");
    p.l(23, 30, 22, 31, "a");
  } },
  chart: { draw: (p) => {
    p.r(2, 2, 2, 28, "d");
    p.r(2, 28, 28, 2, "d");
    p.r(7, 20, 5, 8, "m");
    p.r(14, 14, 5, 14, "m");
    p.r(21, 8, 5, 20, "m");
    p.r(7, 20, 5, 1, "h");
    p.r(14, 14, 5, 1, "h");
    p.r(21, 8, 5, 1, "h");
    p.l(5, 22, 12, 15, "a", 2);
    p.l(12, 15, 16, 18, "a", 2);
    p.l(16, 18, 26, 6, "a", 2);
    p.p([[22, 3], [31, 1], [29, 10]], "a");
  } },
  ballot: { draw: (p) => {
    p.r(11, 0, 11, 15, "w");
    p.l(13, 7, 15, 10, "a", 2);
    p.l(15, 10, 20, 3, "a", 2);
    p.p([[3, 15], [8, 11], [24, 11], [29, 15]], "s");
    p.r(3, 15, 26, 17, "m");
    p.r(9, 13, 14, 2, "k");
    p.r(11, 11, 11, 2, "w");
    p.star(16, 23, 5, "a");
    p.r(3, 15, 26, 1, "h");
  } },
};

/** The motif a theme draws; every theme in themes.json has its own entry in MOTIFS. */
export function motifFor(theme: string): string {
  const m = C.theme[theme]?.motif;
  return m && MOTIFS[m] ? m : "star";
}

const motifCache = new Map<string, Bitmap>();

/** A motif drawn in the given inks, shaded (unless flat) and outlined: 34×34. */
function motifSprite(id: string, inks: Inks, flat: boolean): Bitmap {
  const key = `${id}|${flat}|${Object.values(inks).join(",")}`;
  const hit = motifCache.get(key);
  if (hit) return hit;
  const pen = new Pen(inks);
  MOTIFS[id].draw(pen);
  const b = pen.b;
  if (!flat) {
    // light from the top left: lit rims there, shade on the bottom right
    const src = new Bitmap(b.w, b.h);
    src.data.set(b.data);
    for (let y = 0; y < MS; y++) for (let x = 0; x < MS; x++) {
      const a = src.alpha(x, y);
      if (a < 200) continue;
      const [r, g, bl] = src.get(x, y);
      const c: RGB = [r, g, bl];
      if (!src.alpha(x + 1, y) || !src.alpha(x, y + 1)) b.set(x, y, shade(c, 0.8));
      else if (!src.alpha(x - 1, y) || !src.alpha(x, y - 1)) b.set(x, y, mix(c, WHITE, 0.22));
    }
  }
  const out = b.outlined(OUTLINE);
  if (motifCache.size > 400) motifCache.clear();
  motifCache.set(key, out);
  return out;
}

// ------------------------------------------------------------------ titles

type Font = "big" | "normal" | "small";

export interface TitleLayout {
  lines: string[];
  font: Font;
  /** pixels between letters */
  gap: number;
  /** a leading "The"/"A"/"An" was left off to make it fit */
  dropped: boolean;
}

/** "Soufflé" → "SOUFFLE": the pixel fonts are plain capitals. */
export function foldTitle(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().replace(/\s+/g, " ").trim();
}

export function titleWidth(s: string, font: Font, gap: number): number {
  if (font === "big") return s.length ? s.length * (5 + gap) * 2 - gap * 2 : 0;
  return font === "normal" ? textWidth(s, gap) : smallTextWidth(s, gap);
}

const TIERS: { font: Font; gap: number; lines: number }[] = [
  { font: "big", gap: 1, lines: 1 },
  { font: "normal", gap: 1, lines: 2 },
  { font: "small", gap: 1, lines: 2 },
  { font: "small", gap: 0, lines: 2 },
];
const ARTICLES = new Set(["THE", "A", "AN"]);

/** Split words into n lines (1 or 2), whole words only, the most even split that fits; null if none fits. */
function split(words: string[], n: number, font: Font, gap: number, maxW: number, force = false): string[] | null {
  if (n === 1 || words.length < 2) {
    const line = words.join(" ");
    return force || titleWidth(line, font, gap) <= maxW ? [line] : null;
  }
  let best: string[] | null = null;
  let bestW = Infinity;
  for (let k = 1; k < words.length; k++) {
    const a = words.slice(0, k).join(" ");
    const b = words.slice(k).join(" ");
    const w = Math.max(titleWidth(a, font, gap), titleWidth(b, font, gap));
    if ((force || w <= maxW) && w < bestW) {
      best = [a, b];
      bestW = w;
    }
  }
  return best;
}

/**
 * Lays a title out in at most two lines without ever cutting a word: the biggest font that fits wins
 * (one big line, then two normal lines, then two small ones). A leading article goes only if nothing else fits.
 */
export function layoutTitle(title: string, maxW = TITLE_W): TitleLayout {
  const all = foldTitle(title).split(" ").filter(Boolean);
  const attempt = (words: string[], dropped: boolean): TitleLayout | null => {
    for (const t of TIERS) for (let n = 1; n <= t.lines; n++) {
      const lines = split(words, n, t.font, t.gap, maxW);
      if (lines) return { lines, font: t.font, gap: t.gap, dropped };
    }
    return null;
  };
  const plain = attempt(all, false);
  if (plain) return plain;
  if (all.length > 1 && ARTICLES.has(all[0])) {
    const shorter = attempt(all.slice(1), true);
    if (shorter) return shorter;
  }
  return { lines: split(all, 2, "small", 0, maxW, true) ?? [all.join(" ")], font: "small", gap: 0, dropped: false };
}

/** Draw a title line: two-tone fill, dark outline, a drop shadow. */
function drawTitleLine(im: Bitmap, x: number, y: number, s: string, font: Font, gap: number, top: RGB, bottom: RGB, shadow: boolean): void {
  const h = font === "big" ? FONT_H * 2 : font === "normal" ? FONT_H : SMALL_H;
  const w = titleWidth(s, font, gap);
  const t = new Bitmap(w + 1, h);
  if (font === "small") drawSmallText(t, 0, 0, s, WHITE, gap);
  else drawText(t, 0, 0, s, WHITE, gap, font === "big" ? 2 : 1);
  const split = Math.ceil(h * 0.55);
  for (let yy = 0; yy < h; yy++) for (let xx = 0; xx < t.w; xx++) if (t.alpha(xx, yy)) t.set(xx, yy, yy < split ? top : bottom);
  const o = t.outlined(OUTLINE);
  if (shadow) {
    for (let yy = 0; yy < o.h; yy++) for (let xx = 0; xx < o.w; xx++) if (o.alpha(xx, yy)) im.set(x - 1 + xx + 1, y - 1 + yy + 1, OUTLINE, 150);
  }
  im.blit(o, x - 1, y - 1);
}

// ------------------------------------------------------------------ scenes

type Far = "hills" | "peaks" | "dunes" | "pines" | "trees" | "acacia" | "skyline" | "deadtrees" | "icebergs" | "mesas" | "ruins" | "stands" | "island" | "volcanoes" | "none";
type Ground = "grass" | "meadow" | "savanna" | "field" | "sand" | "water" | "road" | "rails" | "lawn" | "snow" | "lava" | "grid" | "clouds" | "wasteland";
type Special = "interior" | "stage" | "cave" | "underwater" | "space";
type Sky = "day" | "dusk" | "night";
interface SceneSpec {
  far?: Far;
  ground?: Ground;
  special?: Special;
  sky: Sky;
  /** a big moon behind the hero */
  moon?: boolean;
}
const S = (far: Far, ground: Ground, sky: Sky = "day", moon = false): SceneSpec => ({ far, ground, sky, moon });
const SP = (special: Special, sky: Sky = "day"): SceneSpec => ({ special, sky });

/** Where each theme's hero stands. */
const SCENES: Record<string, SceneSpec> = {
  wild_animals: S("acacia", "savanna"), pets_vets: S("hills", "meadow"), gardens: S("trees", "meadow"), farming: S("hills", "field"),
  cooking: SP("interior"), trains: S("peaks", "rails"), pirates: S("island", "water"), space: SP("space", "night"),
  detectives: S("skyline", "road", "night"), haunted_house: S("deadtrees", "grass", "night", true), sports: S("stands", "lawn"),
  fairy_tales: S("hills", "meadow", "dusk"), dungeon_crawl: SP("cave", "night"), vikings: S("peaks", "water", "dusk"),
  cyberpunk: S("skyline", "grid", "night"), dinosaurs: S("volcanoes", "grass"), ocean: SP("underwater"), wizards: S("peaks", "grass", "night"),
  kaiju: S("skyline", "road", "dusk"), castles: S("hills", "grass"), wild_west: S("mesas", "sand", "dusk"), ancient_egypt: S("dunes", "sand"),
  ancient_empire: S("ruins", "grass"), jungle: S("trees", "grass"), polar: S("icebergs", "snow"), circus: SP("stage"),
  music_bands: SP("stage", "night"), movie_studio: SP("stage", "dusk"), racing: S("stands", "road"), aviation: S("none", "clouds"),
  lighthouse: S("none", "water", "dusk"), bees: S("hills", "meadow"), birds: S("trees", "meadow"), mushrooms: S("pines", "grass", "dusk"),
  tea_cafe: SP("interior"), vineyards: S("hills", "field", "dusk"), city_building: S("skyline", "road"), mining: SP("cave"),
  aliens: S("hills", "field", "night"), time_travel: S("ruins", "grid", "night"), post_apocalypse: S("ruins", "wasteland", "dusk"),
  zombies: S("deadtrees", "grass", "night", true), robots: S("skyline", "grid"), steampunk: S("skyline", "rails", "dusk"),
  myths: S("none", "clouds", "dusk"), samurai: S("peaks", "grass", "dusk"), spies: S("skyline", "road", "night"), heists: S("skyline", "road", "night"),
  archaeology: S("ruins", "sand"), volcano: S("volcanoes", "lava", "dusk"), caravans: S("dunes", "sand", "dusk"), cute_monsters: S("hills", "meadow"),
  witches: S("deadtrees", "grass", "night", true), vampires: S("deadtrees", "grass", "night", true), dragons: S("peaks", "grass", "dusk"),
  toys: SP("interior"), weather: S("hills", "grass"), mountains: S("peaks", "snow"), stock_market: S("skyline", "road"), elections: SP("stage"),
};

function sceneFor(theme: string): SceneSpec {
  const s = SCENES[theme];
  if (s) return s;
  const tags = C.theme[theme]?.tags ?? [];
  if (tags.includes("science")) return S("skyline", "grid", "night");
  if (tags.includes("mystery")) return S("deadtrees", "grass", "night", true);
  return S("hills", "grass");
}

interface Ctx {
  im: Bitmap;
  rnd: () => number;
  hy: number;
  top: RGB;
  bot: RGB;
  far: RGB;
  ground: RGB;
  dk: RGB;
  lt: RGB;
  main: RGB;
  acc: RGB;
  flat: boolean;
}

const BAYER = [0.125, 0.625, 0.875, 0.375];

/** a banded, dithered vertical gradient over rows y0..y1 */
function gradient(im: Bitmap, y0: number, y1: number, a: RGB, b: RGB, steps = 6, flat = false): void {
  for (let y = y0; y <= y1; y++) for (let x = 0; x < im.w; x++) {
    if (flat) {
      im.set(x, y, mix(a, b, 0.4));
      continue;
    }
    const lv = clamp01((y - y0) / Math.max(1, y1 - y0)) * steps;
    const lo = Math.floor(lv);
    const step = lv - lo > BAYER[(y & 1) * 2 + (x & 1)] ? lo + 1 : lo;
    im.set(x, y, mix(a, b, Math.min(steps, step) / steps));
  }
}

const NAT = {
  grass: [92, 150, 72] as RGB, dry: [196, 168, 92] as RGB, sand: [222, 190, 128] as RGB, water: [52, 112, 176] as RGB,
  snow: [232, 240, 246] as RGB, rock: [92, 86, 100] as RGB, road: [76, 74, 86] as RGB, wood: [150, 100, 62] as RGB,
  lava: [64, 40, 44] as RGB, cloud: [246, 244, 252] as RGB, dark: [30, 26, 44] as RGB,
};

function groundTone(g: Ground | undefined, pal: RGB[]): RGB {
  const tint = shade(pal[1], 0.85);
  const nat = g === "meadow" || g === "grass" || g === "lawn" || g === "field" ? NAT.grass
    : g === "savanna" ? NAT.dry : g === "sand" ? NAT.sand : g === "water" ? NAT.water : g === "snow" ? NAT.snow
    : g === "road" || g === "rails" ? NAT.grass : g === "lava" ? NAT.lava : g === "grid" ? NAT.dark : g === "clouds" ? NAT.cloud
    : g === "wasteland" ? [150, 120, 90] as RGB : NAT.grass;
  return mix(nat, tint, g === "snow" || g === "clouds" ? 0.15 : 0.35);
}

function drawStars(c: Ctx, y1: number, n: number): void {
  for (let i = 0; i < n; i++) {
    const x = Math.floor(c.rnd() * c.im.w);
    const y = 4 + Math.floor(c.rnd() * (y1 - 4));
    const tw = c.rnd() < 0.15;
    const col = mix(WHITE, c.acc, 0.2);
    c.im.set(x, y, col, tw ? 255 : 170);
    if (tw) {
      c.im.set(x - 1, y, col, 120);
      c.im.set(x + 1, y, col, 120);
      c.im.set(x, y - 1, col, 120);
      c.im.set(x, y + 1, col, 120);
    }
  }
}

function sunOrMoon(c: Ctx, sky: Sky, x: number, y: number, r: number, big: boolean): void {
  const im = c.im;
  if (sky === "night") {
    const col = mix([250, 240, 200], c.acc, 0.12);
    if (big) {
      im.ellipse(x - r - 3, y - r - 3, x + r + 3, y + r + 3, col, 50);
      im.ellipse(x - r, y - r, x + r, y + r, col);
      im.ellipse(x - r / 2, y - r / 3, x - r / 2 + 3, y - r / 3 + 2, shade(col, 0.9));
      im.ellipse(x + 2, y + 3, x + 5, y + 5, shade(col, 0.9));
    } else {
      im.ellipse(x - r, y - r, x + r, y + r, col);
      im.ellipse(x - r + 3, y - r - 1, x + r + 3, y + r - 1, c.top);
    }
  } else {
    const col: RGB = sky === "dusk" ? [255, 176, 96] : [255, 240, 196];
    im.ellipse(x - r - 2, y - r - 2, x + r + 2, y + r + 2, col, 70);
    im.ellipse(x - r, y - r, x + r, y + r, col);
    if (sky === "dusk") for (let k = 0; k < 3; k++) im.rect(x - r, y + 1 + k * 3, 2 * r + 1, 1, c.bot, 200);
  }
}

function drawFar(c: Ctx, far: Far): void {
  const { im, rnd, hy } = c;
  const fc = c.far;
  const fd = shade(fc, 0.88);
  switch (far) {
    case "hills":
      for (let i = 0; i < 5; i++) {
        const cx = i * 24 + rnd() * 14 - 6;
        const r = 16 + rnd() * 10;
        im.ellipse(cx - r, hy - 5 - rnd() * 7, cx + r, hy + 24, i % 2 ? fc : fd);
      }
      break;
    case "peaks":
    case "volcanoes":
      for (let i = 0; i < 5; i++) {
        const x = i * 22 + rnd() * 10 - 4;
        const h = 12 + rnd() * 12;
        const w = 16 + rnd() * 6;
        im.poly([[x - w, hy + 1], [x, hy - h], [x + w, hy + 1]], fc);
        im.poly([[x, hy - h], [x + w, hy + 1], [x + 3, hy + 1]], fd);
        if (far === "peaks") im.poly([[x - 4.5, hy - h + 6], [x, hy - h], [x + 4.5, hy - h + 6], [x + 1.5, hy - h + 4.5], [x - 1.5, hy - h + 6.5]], mix(fc, WHITE, 0.65));
        else {
          im.rect(x - 2, hy - h, 5, 2, [230, 96, 56]);
          for (let k = 0; k < 4; k++) im.ellipse(x - 4 + k * 3, hy - h - 6 - k * 4, x + 2 + k * 3, hy - h - 1 - k * 4, mix(c.top, [120, 110, 120], 0.5), 150);
        }
      }
      break;
    case "dunes":
      for (const [base, amp, ph, col] of [[hy - 4, 4, rnd() * 6, fc], [hy + 2, 3, rnd() * 6, fd]] as const) {
        for (let x = 0; x < im.w; x++) {
          const top = Math.round(base + Math.sin(x / 11 + ph) * amp);
          im.rect(x, top, 1, im.h - top, col);
        }
      }
      break;
    case "pines":
    case "trees":
      for (let x = -4; x < im.w + 4; x += 6 + Math.floor(rnd() * 4)) {
        const h = 8 + rnd() * 10;
        const col = rnd() < 0.5 ? fc : fd;
        if (far === "pines") im.poly([[x - 4, hy + 1], [x, hy - h], [x + 4, hy + 1]], col);
        else {
          im.rect(x, hy - h / 2, 2, h / 2 + 1, shade(col, 0.8));
          im.ellipse(x - 5, hy - h, x + 6, hy - h / 2 + 3, col);
        }
      }
      break;
    case "acacia":
      im.rect(0, hy - 1, im.w, 2, fc);
      for (let i = 0; i < 3; i++) {
        const x = 10 + i * 34 + rnd() * 10;
        const h = 8 + rnd() * 6;
        im.line(x, hy, x + 1, hy - h, fd);
        im.line(x + 1, hy - h + 3, x + 4, hy - h, fd);
        im.ellipse(x - 8, hy - h - 3, x + 9, hy - h + 1, fd);
      }
      break;
    case "skyline": {
      let x = -2;
      while (x < im.w) {
        const w = 6 + Math.floor(rnd() * 8);
        const h = 10 + Math.floor(rnd() * 18);
        const col = rnd() < 0.5 ? fc : fd;
        im.rect(x, hy - h, w, h + 1, col);
        if (rnd() < 0.3) im.rect(x + Math.floor(w / 2), hy - h - 4, 1, 4, col);
        for (let wy = hy - h + 2; wy < hy - 2; wy += 3) for (let wx = x + 1; wx < x + w - 1; wx += 2) if (rnd() < 0.35) im.set(wx, wy, [255, 224, 140], 220);
        x += w + Math.floor(rnd() * 2);
      }
      break;
    }
    case "deadtrees":
      im.rect(0, hy - 2, im.w, 3, fd);
      for (let i = 0; i < 4; i++) {
        const x = 6 + i * 26 + rnd() * 10;
        const h = 10 + rnd() * 10;
        im.line(x, hy, x, hy - h, fd);
        im.line(x + 1, hy, x + 1, hy - h + 2, fd);
        im.line(x, hy - h * 0.6, x - 5, hy - h * 0.9, fd);
        im.line(x, hy - h * 0.75, x + 5, hy - h, fd);
        im.line(x - 3, hy - h * 0.78, x - 5, hy - h * 0.7, fd);
      }
      for (let i = 0; i < 5; i++) {
        const x = Math.floor(rnd() * im.w);
        im.rect(x, hy - 4, 4, 5, fc);
        im.rect(x + 1, hy - 5, 2, 1, fc);
      }
      break;
    case "icebergs":
      for (let i = 0; i < 4; i++) {
        const x = 8 + i * 26 + rnd() * 8;
        const h = 4 + rnd() * 8;
        im.poly([[x - 9, hy + 1], [x - 3, hy - h], [x + 2, hy - h + 2], [x + 10, hy + 1]], mix(WHITE, c.far, 0.25));
        im.poly([[x + 2, hy - h + 2], [x + 10, hy + 1], [x + 2, hy + 1]], mix(WHITE, c.far, 0.5));
      }
      break;
    case "mesas":
      for (let i = 0; i < 3; i++) {
        const x = 4 + i * 34 + rnd() * 10;
        const w = 12 + rnd() * 10;
        const h = 8 + rnd() * 10;
        im.poly([[x - 4, hy + 1], [x, hy - h], [x + w, hy - h], [x + w + 4, hy + 1]], i % 2 ? fc : fd);
        im.rect(x, hy - h, w, 1, mix(fc, WHITE, 0.25));
      }
      break;
    case "ruins":
      im.rect(0, hy - 1, im.w, 2, fd);
      for (let i = 0; i < 5; i++) {
        const x = 4 + i * 20 + rnd() * 8;
        const h = 6 + rnd() * 12;
        im.rect(x, hy - h, 4, h + 1, fc);
        im.rect(x - 1, hy - h, 6, 1, fc);
        im.set(x + 3, hy - h - 1, fc);
        if (rnd() < 0.5) im.rect(x + 4, hy - h + 2, 8, 2, fd);
      }
      break;
    case "stands":
      im.rect(0, hy - 12, im.w, 13, fd);
      for (let y = hy - 11; y < hy; y += 2) for (let x = (y & 2) ? 0 : 1; x < im.w; x += 2) {
        const r = c.rnd();
        im.set(x, y, r < 0.33 ? c.acc : r < 0.66 ? mix(fc, WHITE, 0.4) : c.main);
      }
      im.rect(0, hy - 13, im.w, 1, shade(fd, 0.8));
      break;
    case "island": {
      const x = rnd() < 0.5 ? 20 : 76;
      im.ellipse(x - 12, hy - 4, x + 12, hy + 4, fd);
      im.line(x, hy - 3, x + 2, hy - 14, shade(fd, 0.8));
      im.line(x + 1, hy - 3, x + 3, hy - 14, shade(fd, 0.8));
      for (const [dx, dy] of [[-6, 2], [6, 3], [-4, -2], [5, -1]] as const) im.line(x + 2, hy - 14, x + 2 + dx, hy - 14 + dy, shade(fd, 0.8));
      break;
    }
    case "none":
      break;
  }
}

const FLOWERS: RGB[] = [[250, 240, 240], [250, 210, 80], [230, 110, 120]];

function drawGround(c: Ctx, g: Ground): void {
  const { im, rnd, hy } = c;
  const H = im.h;
  const vx = im.w / 2;
  switch (g) {
    case "grass":
    case "meadow":
    case "savanna":
    case "sand":
    case "snow":
    case "wasteland":
      gradient(im, hy, H - 1, c.lt, c.dk, 3, c.flat);
      for (let i = 0; i < 40; i++) {
        const x = Math.floor(rnd() * im.w);
        const y = hy + 2 + Math.floor(rnd() * (H - hy - 2));
        if (g === "sand" || g === "snow" || g === "wasteland") im.set(x, y, shade(c.ground, 0.9));
        else {
          im.set(x, y, shade(c.ground, 0.8));
          im.set(x, y - 1, shade(c.ground, 0.8));
        }
      }
      if (g === "meadow") for (let i = 0; i < 18; i++) im.set(Math.floor(rnd() * im.w), hy + 3 + Math.floor(rnd() * (H - hy - 3)), FLOWERS[i % 3]);
      if (g === "wasteland") for (let i = 0; i < 6; i++) im.rect(Math.floor(rnd() * im.w), hy + 4 + Math.floor(rnd() * 12), 3, 1, shade(c.ground, 0.7));
      break;
    case "field":
    case "lawn":
      for (let y = hy; y < H; y++) for (let x = 0; x < im.w; x++) {
        const u = (x - vx) / (y - hy + 3);
        const band = Math.floor(u * (g === "field" ? 3 : 1.6) + 100) % 2;
        const t = (y - hy) / (H - hy);
        im.set(x, y, mix(band ? c.lt : c.ground, c.dk, t * 0.6));
      }
      break;
    case "water":
      for (let y = hy; y < H; y++) {
        const t = (y - hy) / (H - hy);
        for (let x = 0; x < im.w; x++) im.set(x, y, mix(mix(c.ground, c.bot, 0.35), shade(c.ground, 0.7), t));
        if (!c.flat && (y - hy) % 3 === 1) for (let x = Math.floor(rnd() * 8); x < im.w; x += 6 + Math.floor(rnd() * 10)) im.rect(x, y, 2 + Math.floor(t * 4), 1, mix(c.ground, WHITE, 0.45));
      }
      break;
    case "road":
    case "rails":
      gradient(im, hy, H - 1, c.lt, c.dk, 3, c.flat);
      for (let y = hy; y < H; y++) {
        const half = (y - hy) * (g === "road" ? 1.3 : 0.9) + 1;
        const road = g === "road" ? NAT.road : mix(NAT.wood, c.dk, 0.5);
        im.rect(vx - half, y, half * 2, 1, road);
        if (g === "road") {
          im.set(vx - half, y, [236, 232, 220]);
          im.set(vx + half - 1, y, [236, 232, 220]);
          if (Math.floor(Math.sqrt(y - hy) * 2.5) % 2 === 0) im.rect(vx - 0.5 - (y - hy) * 0.04, y, 1 + (y - hy) * 0.08, 1, [240, 210, 90]);
        } else {
          if (Math.floor(Math.sqrt(y - hy) * 3) % 2 === 0) im.rect(vx - half, y, half * 2, 1, mix(NAT.wood, WHITE, 0.1));
          im.set(vx - half * 0.7, y, [200, 200, 210]);
          im.set(vx + half * 0.7, y, [200, 200, 210]);
        }
      }
      break;
    case "lava":
      gradient(im, hy, H - 1, mix(c.ground, c.far, 0.3), shade(c.ground, 0.7), 3, c.flat);
      for (let i = 0; i < 6; i++) {
        let x = rnd() * im.w;
        let y = hy + 2 + rnd() * 6;
        for (let k = 0; k < 14; k++) {
          im.set(x, y, k % 3 ? [250, 120, 50] : [255, 210, 90]);
          x += rnd() * 3 - 1;
          y += rnd() < 0.6 ? 1 : 0;
        }
      }
      break;
    case "grid": {
      gradient(im, hy, H - 1, shade(c.ground, 1.3), c.ground, 3, c.flat);
      const line = mix(c.acc, WHITE, 0.2);
      im.rect(0, hy, im.w, 1, line);
      for (let k = 1; k < 8; k++) {
        const y = hy + Math.round(k * k * 0.42);
        if (y < H) im.rect(0, y, im.w, 1, line, 200);
      }
      for (let k = -8; k <= 8; k++) im.line(vx + k * 3, hy, vx + k * 22, H, line, 200);
      break;
    }
    case "clouds":
      for (let i = 0; i < 9; i++) {
        const x = i * 12 + rnd() * 6 - 4;
        const r = 7 + rnd() * 6;
        im.ellipse(x - r, hy - r * 0.6, x + r, hy + r, mix(NAT.cloud, c.bot, 0.35));
      }
      im.rect(0, hy + 4, im.w, H - hy - 4, mix(NAT.cloud, c.bot, 0.3));
      for (let i = 0; i < 10; i++) {
        const x = i * 10 + rnd() * 6 - 4;
        const r = 6 + rnd() * 5;
        im.ellipse(x - r, hy + 6 - r * 0.5, x + r, hy + 8 + r, NAT.cloud);
      }
      im.rect(0, hy + 12, im.w, H - hy - 12, NAT.cloud);
      for (let i = 0; i < 3; i++) {
        const x = 8 + rnd() * 80;
        const y = 24 + rnd() * 12;
        im.ellipse(x - 7, y - 2, x + 7, y + 2, mix(NAT.cloud, c.bot, 0.2), 200);
      }
      break;
  }
}

function drawSpecial(c: Ctx, s: Special): void {
  const { im, rnd, hy } = c;
  const H = im.h;
  switch (s) {
    case "interior": {
      const wall = mix(c.top, CREAM, 0.25);
      gradient(im, 0, hy, shade(wall, 0.85), wall, 4, c.flat);
      for (let x = 2; x < im.w; x += 8) im.rect(x, 0, 2, hy, shade(wall, 0.94));
      // a window onto a sunny day
      const wx = rnd() < 0.5 ? 10 : 68;
      im.box(wx, 22, wx + 17, 40, [176, 214, 240], OUTLINE);
      im.rect(wx + 1, 35, 16, 5, [130, 190, 110]);
      im.ellipse(wx + 11, 25, wx + 15, 29, [255, 240, 196]);
      im.rect(wx + 8, 23, 1, 17, CREAM);
      im.rect(wx + 1, 31, 16, 1, CREAM);
      im.rect(wx - 1, 41, 20, 2, shade(c.acc, 0.8));
      im.rect(0, hy - 2, im.w, 2, shade(wall, 0.7));
      for (let y = hy; y < H; y++) for (let x = 0; x < im.w; x++) {
        const u = Math.floor((x - im.w / 2) / ((y - hy + 6) * 0.45) + 100);
        const v = Math.floor(Math.log(y - hy + 6) * 5);
        im.set(x, y, (u + v) % 2 ? mix(c.acc, CREAM, 0.55) : shade(mix(c.acc, CREAM, 0.2), 0.75));
      }
      break;
    }
    case "stage": {
      gradient(im, 0, hy, shade(c.top, 0.5), shade(c.top, 0.85), 4, c.flat);
      const curtain = mix(c.acc, [200, 40, 50], 0.4);
      for (let x = 0; x < im.w; x++) {
        const d = x < im.w / 2 ? x : im.w - 1 - x;
        if (d > 15) continue;
        const fold = Math.sin(x * 1.1) * 0.5 + 0.5;
        im.rect(x, 0, 1, hy + 2 - Math.max(0, d - 10) * 2, mix(shade(curtain, 0.6), curtain, fold));
      }
      for (let x = 0; x < im.w; x += 8) im.ellipse(x - 1, 2, x + 8, 9, shade(curtain, 0.85));
      im.poly([[40, 0], [56, 0], [80, hy + 10], [16, hy + 10]], [255, 246, 214], 34);
      for (let y = hy; y < H; y++) for (let x = 0; x < im.w; x++) {
        const plank = Math.floor((y - hy) / 3);
        const seam = (x + plank * 7) % 18 === 0 || (y - hy) % 3 === 0;
        im.set(x, y, seam ? shade(NAT.wood, 0.7) : mix(NAT.wood, c.dk, (y - hy) / (H - hy) * 0.5));
      }
      im.ellipse(30, hy + 4, 66, hy + 16, [255, 246, 214], 50);
      break;
    }
    case "cave": {
      const rock = mix(NAT.rock, c.top, 0.35);
      gradient(im, 0, hy, shade(rock, 0.5), shade(rock, 0.8), 4, c.flat);
      im.ellipse(30, 14, 66, hy + 8, mix(c.bot, rock, 0.4), 160);
      for (let x = 0; x < im.w; x += 5 + Math.floor(rnd() * 5)) {
        const h = 4 + rnd() * 10;
        im.poly([[x - 3, 0], [x + 3, 0], [x, h]], shade(rock, 0.6));
      }
      gradient(im, hy, H - 1, shade(rock, 0.9), shade(rock, 0.55), 3, c.flat);
      for (let i = 0; i < 5; i++) {
        const x = 4 + rnd() * 88;
        const y = hy + 3 + rnd() * 12;
        im.poly([[x - 2, y + 3], [x, y - 4], [x + 2, y + 3]], mix(c.acc, WHITE, 0.3));
        im.set(x, y - 2, WHITE);
      }
      for (const x of [14, 82]) {
        im.rect(x, hy - 10, 1, 8, NAT.wood);
        im.ellipse(x - 3, hy - 14, x + 3, hy - 8, [255, 190, 80], 200);
        im.ellipse(x - 6, hy - 17, x + 6, hy - 5, [255, 190, 80], 50);
      }
      break;
    }
    case "underwater": {
      const deep = mix(NAT.water, c.top, 0.35);
      gradient(im, 0, H - 1, mix(deep, WHITE, 0.3), shade(deep, 0.55), 7, c.flat);
      for (let i = 0; i < 4; i++) {
        const x = 10 + i * 24 + rnd() * 8;
        im.poly([[x, 0], [x + 6, 0], [x + 20, hy], [x + 10, hy]], WHITE, 26);
      }
      im.rect(0, hy + 8, im.w, H - hy - 8, mix(NAT.sand, deep, 0.35));
      for (let x = 0; x < im.w; x++) im.set(x, hy + 7 + Math.round(Math.sin(x / 7) * 1.2), mix(NAT.sand, deep, 0.2));
      for (let i = 0; i < 6; i++) {
        const x = 4 + rnd() * 88;
        const h = 8 + rnd() * 10;
        for (let y = 0; y < h; y++) im.rect(x + Math.round(Math.sin(y / 2.5 + i) * 1.5), hy + 9 - y, 2, 1, mix(NAT.grass, deep, 0.3));
      }
      for (let i = 0; i < 9; i++) {
        const x = rnd() * im.w;
        const y = 12 + rnd() * 40;
        const r = 1 + Math.floor(rnd() * 2);
        im.ellipse(x - r, y - r, x + r, y + r, mix(WHITE, deep, 0.3), 150);
        im.set(x - r + 1, y - r + 1, WHITE);
      }
      break;
    }
    case "space": {
      gradient(im, 0, H - 1, mix(NAT.dark, c.top, 0.5), mix(c.top, c.acc, 0.25), 5, c.flat);
      im.ellipse(-10, 14, 50, 44, c.acc, 30);
      im.ellipse(50, 6, 100, 30, c.main, 22);
      drawStars(c, H - 8, 46);
      // a ringed planet in the sky and the curve of a moon underfoot
      const px = rnd() < 0.5 ? 16 : 80;
      im.ellipse(px - 6, 28, px + 6, 40, mix(c.acc, WHITE, 0.2));
      im.ellipse(px - 6, 28, px + 2, 36, mix(c.acc, WHITE, 0.45));
      im.line(px - 11, 37, px + 11, 31, mix(c.main, WHITE, 0.3));
      im.line(px - 10, 38, px + 11, 32, shade(mix(c.main, WHITE, 0.3), 0.8));
      const moon = mix([170, 160, 180], c.top, 0.25);
      im.ellipse(-40, hy + 4, im.w + 40, hy + 90, moon);
      im.ellipse(-40, hy + 4, im.w + 40, hy + 8, mix(moon, WHITE, 0.25));
      for (let i = 0; i < 7; i++) {
        const x = rnd() * im.w;
        const y = hy + 8 + rnd() * 12;
        im.ellipse(x - 3, y - 1, x + 3, y + 1, shade(moon, 0.8));
      }
      break;
    }
  }
}

// ------------------------------------------------------------------ frames

type Frame = "kids" | "family" | "party" | "hobby" | "hardcore" | "collectors" | "roleplayers";
const FRAMES: Frame[] = ["kids", "family", "party", "hobby", "hardcore", "collectors", "roleplayers"];
const GOLD: RGB = [232, 184, 72];
const GOLD_DK: RGB = [168, 120, 40];

function inBand(x: number, y: number, w: number, h: number, d: number): boolean {
  return x < d || y < d || x >= w - d || y >= h - d;
}

function drawFrame(im: Bitmap, aud: Frame, pal: RGB[], acc: RGB): void {
  const W = im.w;
  const H = im.h;
  const band = (f: (x: number, y: number) => RGB | null) => {
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (inBand(x, y, W, H, F)) {
      const c = f(x, y);
      if (c) im.set(x, y, c);
    }
  };
  const vivid = [...pal].sort((a, b) => sat(b) - sat(a))[0];
  switch (aud) {
    case "kids": {
      // rounded, bright, with dots
      const fc = mix(vivid, [255, 220, 80], 0.25);
      band((x, y) => ((x + y) % 6 === 0 && (x === 2 || y === 2 || x === W - 3 || y === H - 3) ? WHITE : fc));
      im.strokeBox(F - 1, F - 1, W - F, H - F, mix(fc, WHITE, 0.6));
      const R = 4;
      for (const [cx, cy, sx, sy] of [[R, R, -1, -1], [W - 1 - R, R, 1, -1], [R, H - 1 - R, -1, 1], [W - 1 - R, H - 1 - R, 1, 1]] as const) {
        for (let dy = 0; dy <= R; dy++) for (let dx = 0; dx <= R; dx++) {
          const x = cx + sx * dx;
          const y = cy + sy * dy;
          const d = Math.hypot(dx, dy);
          if (d > R + 0.5) im.clear(x, y);
          else if (d > R - 0.5) im.set(x, y, OUTLINE);
        }
      }
      for (let x = R; x < W - R; x++) {
        im.set(x, 0, OUTLINE);
        im.set(x, H - 1, OUTLINE);
      }
      for (let y = R; y < H - R; y++) {
        im.set(0, y, OUTLINE);
        im.set(W - 1, y, OUTLINE);
      }
      return;
    }
    case "family":
      // classic: a cream border with a thin coloured rule inside
      band(() => CREAM);
      im.strokeBox(F - 1, F - 1, W - F, H - F, shade(acc, 0.8));
      im.strokeBox(F, F, W - F - 1, H - F - 1, OUTLINE);
      break;
    case "party": {
      // bold diagonal stripes
      const a = mix(vivid, [240, 60, 90], 0.3);
      const b: RGB = [255, 222, 70];
      band((x, y) => (Math.floor((x + y) / 4) % 2 ? a : b));
      im.strokeBox(F, F, W - F - 1, H - F - 1, OUTLINE);
      break;
    }
    case "hobby":
    case "hardcore": {
      // dark premium frame and a thin gold rule
      const base: RGB = aud === "hobby" ? mix([40, 34, 46], pal[0], 0.2) : [20, 18, 24];
      band((x, y) => (x === 2 || y === 2 || x === W - 3 || y === H - 3 ? GOLD : base));
      for (const x of [1, W - 2]) for (let y = 2; y < H - 2; y++) im.set(x, y, mix(base, WHITE, 0.06));
      im.strokeBox(F, F, W - F - 1, H - F - 1, OUTLINE);
      if (aud === "hardcore") {
        // gold corner brackets, and a second rule along the art
        for (const [cx, cy, sx, sy] of [[1, 1, 1, 1], [W - 2, 1, -1, 1], [1, H - 2, 1, -1], [W - 2, H - 2, -1, -1]] as const) {
          for (let k = 0; k < 9; k++) {
            im.set(cx + sx * k, cy, GOLD);
            im.set(cx, cy + sy * k, GOLD);
          }
          im.set(cx + sx, cy + sy, [255, 230, 140]);
        }
        im.strokeBox(F + 1, F + 1, W - F - 2, H - F - 2, GOLD_DK);
      }
      break;
    }
    case "collectors": {
      // foil: a metallic sheen running diagonally round the frame
      band((x, y) => {
        const v = Math.abs((((x - y * 1.3) / 14) % 1 + 1) % 1 - 0.5) * 2;
        const glint = (x * 7 + y * 13) % 29 === 0;
        return glint ? WHITE : mix(mix(GOLD_DK, [190, 196, 214], 0.35), [255, 250, 230], 1 - v);
      });
      im.strokeBox(F, F, W - F - 1, H - F - 1, OUTLINE);
      // and a foil streak across the whole cover
      for (let y = F + 1; y < H - F - 1; y++) for (let x = F + 1; x < W - F - 1; x++) {
        const d = (x + y * 0.8) - 70;
        if (d > 0 && d < 7) im.set(x, y, WHITE, d < 2 ? 70 : 34);
      }
      break;
    }
    case "roleplayers": {
      // leather with stitching and brass studs
      const leather: RGB = mix([104, 64, 42], pal[0], 0.15);
      band((x, y) => {
        const edge = x === 2 || y === 2 || x === W - 3 || y === H - 3;
        return edge && (x + y) % 3 !== 0 ? [214, 184, 132] : (x * 3 + y * 5) % 11 === 0 ? shade(leather, 0.85) : leather;
      });
      im.strokeBox(F, F, W - F - 1, H - F - 1, OUTLINE);
      for (const [x, y] of [[2, 2], [W - 3, 2], [2, H - 3], [W - 3, H - 3]] as const) {
        im.ellipse(x - 2, y - 2, x + 2, y + 2, GOLD_DK);
        im.ellipse(x - 1, y - 1, x + 1, y + 1, GOLD);
        im.set(x - 1, y - 1, [255, 236, 160]);
      }
      break;
    }
  }
  im.strokeBox(0, 0, W - 1, H - 1, OUTLINE);
}

function sat(c: RGB): number {
  const mx = Math.max(...c);
  const mn = Math.min(...c);
  return mx === 0 ? 0 : (mx - mn) / mx;
}

// ------------------------------------------------------------------ the cover

export interface BoxArtOpts {
  title: string;
  theme: string;
  mechanic: string;
  publisherColor?: string;
  seed?: number;
  /** 0..1, the art quality of the project: low quality = bland cover. */
  quality?: number;
  /** won a Dicefall award: a gold seal on the cover */
  award?: boolean;
  /** who the game is for; sets the frame. Defaults to the audience the theme suits best. */
  audience?: string;
}

function hashStr(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** The frame an audience gets (unknown audiences fall back to the theme's best fit, then family). */
export function frameFor(audience: string | undefined, theme: string): Frame {
  if (audience && (FRAMES as string[]).includes(audience)) return audience as Frame;
  const fit = C.theme[theme]?.audience as Record<string, number> | undefined;
  if (fit) {
    let best: Frame = "family";
    let bestV = -1;
    for (const f of FRAMES) if ((fit[f] ?? 0) > bestV) {
      best = f;
      bestV = fit[f] ?? 0;
    }
    return best;
  }
  return "family";
}

const cache = new Map<string, Bitmap>();

/** A 96×72 box cover. Same inputs, same cover. */
export function boxArt(o: BoxArtOpts): Bitmap {
  const key = JSON.stringify([o.title, o.theme, o.mechanic, o.publisherColor ?? "", o.seed ?? 0, o.quality ?? 0.6, !!o.award, o.audience ?? ""]);
  const hit = cache.get(key);
  if (hit) return hit;
  const im = drawCover(o);
  if (cache.size > 300) cache.clear();
  cache.set(key, im);
  return im;
}

function drawCover(o: BoxArtOpts): Bitmap {
  const W = BOX_W;
  const H = BOX_H;
  const t = C.theme[o.theme];
  const pal = (t?.palette ?? ["#E85848", "#22968C", "#FAF6EE"]).map(hex);
  const rnd = artRng((o.seed ?? 0) * 7919 + hashStr(`${o.theme}|${o.title}`));
  const q = o.quality ?? 0.6;
  const flat = q < 0.3;
  const fancy = q >= 0.75;
  const im = new Bitmap(W, H);
  const spec = sceneFor(o.theme);

  // colours: the hero takes the palette colour that stands out most against the sky
  const sky0 = pal[0];
  const [c1, c2] = Math.abs(lum(pal[1]) - lum(sky0)) > Math.abs(lum(pal[2]) - lum(sky0)) ? [pal[1], pal[2]] : [pal[2], pal[1]];
  let main = c1;
  if (Math.abs(lum(main) - lum(sky0)) < 50) main = lum(sky0) > 128 ? shade(main, 0.6) : mix(main, WHITE, 0.5);
  const acc = c2;
  const dark = lum(sky0) < 70;
  let top: RGB;
  let bot: RGB;
  if (spec.sky === "night") {
    top = mix(shade(sky0, dark ? 0.8 : 0.4), [14, 12, 36], 0.45);
    bot = mix(shade(sky0, dark ? 1 : 0.65), acc, 0.22);
  } else if (spec.sky === "dusk") {
    top = shade(mix(sky0, [70, 50, 110], 0.35), dark ? 1 : 0.8);
    bot = mix(sky0, [255, 170, 110], 0.5);
  } else {
    top = dark ? sky0 : shade(sky0, 0.74);
    bot = dark ? mix(sky0, acc, 0.4) : mix(sky0, [255, 250, 236], 0.45);
  }
  const ground = groundTone(spec.ground, pal);
  const c: Ctx = {
    im, rnd, hy: 46 + Math.floor(rnd() * 5), top, bot, main, acc, flat,
    far: mix(mix(ground, shade(sky0, 0.8), 0.35), bot, 0.4), ground, dk: shade(ground, 0.78), lt: mix(ground, WHITE, 0.1),
  };
  const frame = frameFor(o.audience, o.theme);
  const followUp = o.title.includes(":");
  const sunX = o.award || rnd() < 0.5 ? 20 : 76;

  // 1. sky and scene
  if (spec.special) drawSpecial(c, spec.special);
  else {
    gradient(im, 0, c.hy, top, bot, 6, flat);
    if (!flat) {
      if (spec.sky === "night") drawStars(c, c.hy - 4, 30);
      const conflict = C.mechanic[o.mechanic]?.tags.includes("conflict") ?? false;
      if (conflict && q >= 0.5) {
        // a sunburst behind everything for games about a fight
        for (let y = 0; y < c.hy; y++) for (let x = 0; x < W; x++) {
          const a = Math.atan2(y - (c.hy - 10), x - W / 2);
          if (Math.floor((a + Math.PI) / 0.2) % 2 === 0) im.set(x, y, WHITE, 22);
        }
      }
      if (spec.moon) sunOrMoon(c, "night", 48, c.hy - 18, 13, true);
      else sunOrMoon(c, spec.sky, sunX, c.hy - 10 - Math.floor(rnd() * 6), 6, false);
      if (spec.sky !== "night" && spec.far !== "none") for (let i = 0; i < 2; i++) {
        const x = 10 + rnd() * 76;
        const y = 22 + rnd() * 14;
        im.ellipse(x - 8, y - 2, x + 8, y + 2, mix(bot, WHITE, 0.5), 150);
        im.ellipse(x - 4, y - 4, x + 3, y, mix(bot, WHITE, 0.5), 150);
      }
      drawFar(c, spec.far ?? "none");
    }
    if (flat) im.rect(0, c.hy, W, H - c.hy, ground);
    else drawGround(c, spec.ground ?? "grass");
  }

  // 2. the title block, measured first so the hero knows where it can stand
  const [gameName, ...rest] = o.title.split(":");
  const subtitle = rest.join(":").trim();
  const maxW = followUp ? TITLE_W - 8 : TITLE_W;
  const lay = layoutTitle(gameName.trim() || o.title, maxW);
  const lineH = lay.font === "big" ? FONT_H * 2 : lay.font === "normal" ? FONT_H : SMALL_H;
  const pitch = lineH + 2;
  const titleY = F + 3;
  let blockBottom = titleY + (lay.lines.length - 1) * pitch + lineH + 1;
  let subLines: string[] = [];
  let subGap = 1;
  if (subtitle) {
    const s = foldTitle(subtitle);
    const room = W - 2 * F - 6;
    if (smallTextWidth(s, 1) <= room) subLines = [s];
    else if (smallTextWidth(s, 0) <= room) {
      subLines = [s];
      subGap = 0;
    } else subLines = split(s.split(" "), 2, "small", 1, room, true) ?? [s];
  }
  const ribbonY = blockBottom + 2;
  if (subLines.length) blockBottom = ribbonY + subLines.length * 7 + 2;

  // 3. the hero
  const id = motifFor(o.theme);
  const motif = MOTIFS[id];
  const inks: Inks = {
    m: flat ? shade(main, 0.9) : main,
    s: flat ? shade(main, 0.9) : lum(main) < 60 ? mix(main, WHITE, 0.2) : shade(main, 0.76),
    a: flat ? shade(main, 0.75) : acc,
    d: mix(OUTLINE, acc, 0.3),
    h: flat ? shade(main, 0.9) : mix(main, WHITE, 0.5),
    k: [52, 42, 58], w: [248, 244, 232], y: [240, 192, 70], g: [96, 160, 76], b: [146, 96, 60], r: [212, 66, 58], q: [150, 150, 164],
  };
  const spr = motifSprite(id, inks, flat);
  const mx = Math.round(W / 2 - spr.w / 2 + (rnd() - 0.5) * 8);
  const floorY = H - F - 3;
  const my = motif.fly ? Math.max(blockBottom + 1, Math.min(floorY - spr.h - 6, 26)) : Math.max(blockBottom - 2, floorY - spr.h + 1);
  // a shadow on the ground (smaller and fainter under things that fly)
  if (!flat && !spec.special?.match(/space|underwater/)) {
    const sw = motif.fly ? 9 : 14;
    im.ellipse(mx + spr.w / 2 - sw, floorY - 2, mx + spr.w / 2 + sw, floorY + 1, OUTLINE, motif.fly ? 50 : 90);
  }
  if (fancy) {
    // a soft glow behind the hero on the best covers
    im.ellipse(mx - 4, my - 4, mx + spr.w + 4, my + spr.h + 4, mix(bot, WHITE, 0.5), 50);
  }
  im.blit(spr, mx, my);

  // 4. the title, two-tone with an outline and shadow
  const tTop: RGB = [252, 248, 236];
  const tBot: RGB = flat ? [230, 224, 212] : mix(tTop, mix(GOLD, acc, 0.35), 0.55);
  lay.lines.forEach((line, i) => {
    const w = titleWidth(line, lay.font, lay.gap);
    drawTitleLine(im, Math.round((W - w) / 2), titleY + i * pitch, line, lay.font, lay.gap, tTop, tBot, !flat);
  });
  // a follow-up's subtitle sits on a ribbon under the name
  if (subLines.length) {
    const rw = Math.min(W - 2 * F, Math.max(...subLines.map((s) => smallTextWidth(s, subGap))) + 6);
    const rx = Math.round((W - rw) / 2);
    const rh = subLines.length * 7 + 2;
    const rc = shade(mix(acc, [190, 50, 60], 0.35), 0.85);
    im.poly([[rx - 4, ribbonY + 2], [rx + 1, ribbonY + 2], [rx + 1, ribbonY + rh + 2], [rx - 4, ribbonY + rh + 2], [rx - 2, ribbonY + rh / 2 + 2]], shade(rc, 0.7));
    im.poly([[rx + rw + 4, ribbonY + 2], [rx + rw - 1, ribbonY + 2], [rx + rw - 1, ribbonY + rh + 2], [rx + rw + 4, ribbonY + rh + 2], [rx + rw + 2, ribbonY + rh / 2 + 2]], shade(rc, 0.7));
    im.box(rx, ribbonY, rx + rw - 1, ribbonY + rh - 1, rc, OUTLINE);
    im.rect(rx + 1, ribbonY + 1, rw - 2, 1, mix(rc, WHITE, 0.3));
    subLines.forEach((s, i) => drawSmallText(im, Math.round((W - smallTextWidth(s, subGap)) / 2), ribbonY + 2 + i * 7, s, tTop, subGap));
  }

  // 5. little extras on the best covers: twinkles round the title
  if (fancy) for (let i = 0; i < 4; i++) {
    const x = F + 3 + Math.floor(rnd() * (W - 2 * F - 6));
    const y = F + 2 + Math.floor(rnd() * (blockBottom - F));
    if (im.get(x, y)[0] + im.get(x, y)[1] > 440) continue;
    im.set(x, y, WHITE);
    im.set(x - 1, y, WHITE, 140);
    im.set(x + 1, y, WHITE, 140);
    im.set(x, y - 1, WHITE, 140);
    im.set(x, y + 1, WHITE, 140);
  }

  // 6. frame by audience
  drawFrame(im, frame, pal, acc);

  // 7. the publisher's mark: a little die in their colour, bottom left
  if (o.publisherColor) {
    const pc = hex(o.publisherColor);
    const x = F + 2;
    const y = H - F - 10;
    im.box(x, y, x + 7, y + 7, pc, OUTLINE);
    im.rect(x + 1, y + 1, 6, 1, mix(pc, WHITE, 0.35));
    const ink: RGB = lum(pc) > 170 ? OUTLINE : WHITE;
    const pips = 1 + (hashStr(o.publisherColor) % 6);
    const at: Record<number, [number, number][]> = {
      1: [[3, 3]], 2: [[1, 1], [5, 5]], 3: [[1, 1], [3, 3], [5, 5]], 4: [[1, 1], [5, 1], [1, 5], [5, 5]],
      5: [[1, 1], [5, 1], [3, 3], [1, 5], [5, 5]], 6: [[1, 1], [5, 1], [1, 3], [5, 3], [1, 5], [5, 5]],
    };
    for (const [px, py] of at[pips]) im.rect(x + px, y + py, 2, 2, ink);
  }

  // 8. a follow-up gets a gold corner flash with a plus
  if (followUp) {
    im.poly([[W - 15, 0], [W, 0], [W, 15]], GOLD);
    im.line(W - 15, 0, W - 1, 14, OUTLINE);
    im.line(W - 13, 0, W - 1, 12, [255, 230, 140]);
    im.rect(W - 6, 2, 1, 5, OUTLINE);
    im.rect(W - 8, 4, 5, 1, OUTLINE);
  }

  // 9. the award seal: a gold rosette with ribbons, on the right
  if (o.award) {
    const cx = W - F - 10;
    const cy = Math.max(blockBottom + 8, 38);
    im.rect(cx - 5, cy + 4, 3, 7, [196, 60, 50]);
    im.rect(cx + 3, cy + 4, 3, 7, [196, 60, 50]);
    im.set(cx - 4, cy + 11, [196, 60, 50]);
    im.set(cx + 4, cy + 11, [196, 60, 50]);
    for (let y = -7; y <= 7; y++) for (let x = -7; x <= 7; x++) {
      const a = Math.atan2(y, x);
      const r = 6 + Math.round(Math.cos(a * 8) * 0.6);
      const d = Math.hypot(x, y);
      if (d <= r + 0.5) im.set(cx + x, cy + y, d > r - 0.6 ? OUTLINE : d >= r - 2 ? GOLD_DK : GOLD);
    }
    im.poly(starPts(cx + 0.5, cy + 0.5, 3.5, 1.6, 5), [255, 236, 160]);
  }
  return im;
}
